import ComponentManager from '@/Management/ComponentManager';
import GroupFactory from '@/Factories/GroupFactory';
import AsyncGroupFactory from '@/Factories/AsyncGroupFactory';
import UIListenerHelper from '@/UIListener/UIListenerHelper';
import Utilities from '@/Utility/Utilities';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import RADApplication from '@/Management/RADApplication';
import Factory from '@/Factories/Factory';
import { IDetailViewGroupDTO } from "coreframetypings/dto";

export default class MetadataGroupFactory extends AsyncGroupFactory {

    public context: DetailViewContext;
    private innerFactory: GroupFactory;

    constructor(private params: any) {
        super(params);
    }

    public async getElementAsync(): Promise<JQuery> {
        if (!this.areChangelogsActive()) {
            this.hideChangelogsAndShowMetadata();
        } else {
            // Set to tabgroup
            MetadataGroupFactory.setUiGenerator(this.detailViewGroup);
        }

        const { groupFactory, tuple } = await GroupFactory.createInstanceAsync(this.detailViewGroup, this.context, {
            isSummaryViewMode: this.isSummaryViewMode,
            locator: this.locator,
        });
        this.innerFactory = groupFactory;
        return tuple?.control;
    }

    /**
     * Hides the metadata group without hiding the changelog group and switches to expander group
     * To be used in onBeforeRender(SummaryView)
     */
    public hideMetadataAndShowChangelogs(): void {
        let changelogs = MetadataGroupFactory.hideMetadataAndShowChangelogs(this.context);
        this.detailViewGroup = changelogs;
    }

    /**
     * Hides the changelog group and switches to expander group
     * To be used in onBeforeRender(SummaryView)
     * @param context
     */
    public hideChangelogsAndShowMetadata(): void {
        let metadata = MetadataGroupFactory.hideChangelogsAndShowMetadata(this.context);
        this.detailViewGroup = metadata;
    }

    /**
     * Hides the metadata group without hiding the changelog group and switches to expander group
     * To be used in onBeforeRender(SummaryView)
     * @param context
     */
    public static hideMetadataAndShowChangelogs(context: DetailViewContext): IDetailViewGroupDTO {
        const pdTypeName = context.getConcretePdTypeName();
        let returnValue: IDetailViewGroupDTO = null;

        if (MetadataGroupFactory.areChangelogsActive(context)) {
            const changelogs = UIListenerHelper.getDetailViewGroupDto(context, `${pdTypeName}.CHANGELOGS`);
            if (!Utilities.isNullOrUndefined(changelogs)) {
                context.detailViewContextDTO.detailView.detailViewGroups.push(changelogs);
            }
            MetadataGroupFactory.setUiGenerator(changelogs);
            UIListenerHelper.removeDetailViewGroupDto(context, `${pdTypeName}.METADATA`);
            returnValue = changelogs;
        }
        
        UIListenerHelper.removeDetailViewGroupDto(context, `${pdTypeName}.METADATA`);

        return returnValue;
    }

    /**
     * Hides the changelog group and switches to expander group
     * To be used in onBeforeRender(SummaryView)
     * @param context
     */
    public static hideChangelogsAndShowMetadata(context: DetailViewContext): IDetailViewGroupDTO {
        const pdTypeName = context.getConcretePdTypeName();
        const metadata = UIListenerHelper.getDetailViewGroupDto(context, `${pdTypeName}.METADATA`);
        
        UIListenerHelper.removeDetailViewGroupDto(context, `${pdTypeName}.CHANGELOGS`);
        MetadataGroupFactory.setUiGenerator(metadata);

        return metadata;
    }

    /**
     * Sets correct UI-Generator for detailview-groups
     */
    private static setUiGenerator(group: IDetailViewGroupDTO): void {
        let uiGenerator = (group.detailViewGroups.length == 0) ? 'ExpanderGroup' : 'TabGroup';

        MetadataGroupFactory.replaceUiGenerator(group, uiGenerator);
        group.detailViewGroups.forEach(g => MetadataGroupFactory.replaceUiGenerator(g, uiGenerator));
    }

    /**
     * Safe replace of UI-Generator
     * @param group
     * @param uiGenerator
     */
    private static replaceUiGenerator(group: IDetailViewGroupDTO, uiGenerator: string): void {
        let params = Factory.parseUIGeneratorQuery(group.uiGenerator);
        params.uiGenerator = uiGenerator;
        group.uiGenerator = Factory.toUIGeneratorQuery(params);
    }

    /**
     * Gibt an ob, die Changelogs für den aktuellen Context sichtbar sein sollen
     */
    private areChangelogsActive(): boolean {
        return MetadataGroupFactory.areChangelogsActive(this.context);
    }

    /**
     * Gibt an ob, die Changelogs für den übergebenen Context sichtbar sein sollen
     * @param context
     */
    private static areChangelogsActive(context: DetailViewContext): boolean {
        const showCommentsInInstance = RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowCommentsInInstance');
        const changeLogDvgItem = UIListenerHelper.getDetailViewItemDto(context, 'Changelogs');

        const isHistorized = context.genericObject.isHistorized;
        return showCommentsInInstance
            && !isHistorized
            && !Utilities.isNullOrUndefined(changeLogDvgItem)
            && changeLogDvgItem.uiGenerator.toLowerCase().indexOf('showcomments=false') == -1;
    }
}

ComponentManager.setFactory('MetadataGroup', MetadataGroupFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');